import React, { createContext, useContext, useReducer, ReactNode } from 'react'
import axios, { AxiosRequestConfig } from 'axios'
import { observer } from 'mobx-react-lite'
import { useStore } from '@hooks/useStore'

// Types
interface ImageUploadState {
  uploading: boolean
  progress: number
  error: string | null
  success: boolean
}

interface UploadOptions {
  maxSize?: number
  allowedTypes?: string[]
  maxWidth?: number
  maxHeight?: number
  additionalData?: Record<string, any>
  headers?: Record<string, string>
  axiosConfig?: AxiosRequestConfig
}

interface DeleteOptions {
  headers?: Record<string, string>
  axiosConfig?: AxiosRequestConfig
}

interface ValidationResult {
  isValid: boolean
  error?: string
}

interface UploadResult {
  success: boolean
  data?: any
  error?: string
}

interface ImageUploadContextType {
  uploading: boolean
  progress: number
  error: string | null
  success: boolean
  uploadImage: (file: File, uploadUrl: string, fieldName?: string, options?: UploadOptions) => Promise<UploadResult>
  deleteImage: (deleteUrl: string, options?: DeleteOptions) => Promise<UploadResult>
  validateFile: (file: File, options?: UploadOptions) => ValidationResult
  clearError: () => void
  clearSuccess: () => void
  reset: () => void
}

interface ImageUploadAction {
  type: string
  payload?: any
}

interface ImageUploadProviderProps {
  children: ReactNode
}

// Initial state
const initialState: ImageUploadState = {
  uploading: false,
  progress: 0,
  error: null,
  success: false,
}

// Action types
const UPLOAD_START = 'UPLOAD_START'
const UPLOAD_PROGRESS = 'UPLOAD_PROGRESS'
const UPLOAD_SUCCESS = 'UPLOAD_SUCCESS'
const UPLOAD_ERROR = 'UPLOAD_ERROR'
const CLEAR_ERROR = 'CLEAR_ERROR'
const CLEAR_SUCCESS = 'CLEAR_SUCCESS'
const RESET = 'RESET'

// Reducer
const imageUploadReducer = (state: ImageUploadState, action: ImageUploadAction): ImageUploadState => {
  switch (action.type) {
    case UPLOAD_START:
      return {
        ...state,
        uploading: true,
        progress: 0,
        error: null,
        success: false,
      }
    case UPLOAD_PROGRESS:
      return {
        ...state,
        progress: action.payload,
      }
    case UPLOAD_SUCCESS:
      return {
        ...state,
        uploading: false,
        progress: 0,
        success: true,
        error: null,
      }
    case UPLOAD_ERROR:
      return {
        ...state,
        uploading: false,
        progress: 0,
        error: action.payload,
        success: false,
      }
    case CLEAR_ERROR:
      return {
        ...state,
        error: null,
      }
    case CLEAR_SUCCESS:
      return {
        ...state,
        success: false,
      }
    case RESET:
      return initialState
    default:
      return state
  }
}

// Context
const ImageUploadContext = createContext<ImageUploadContextType | undefined>(undefined)

// Provider component
export const ImageUploadProvider: React.FC<ImageUploadProviderProps> = observer(({ children }) => {
  const [state, dispatch] = useReducer(imageUploadReducer, initialState)
  const { api } = useStore()
  // Upload image function
  const uploadImage = async (
    file: File,
    uploadUrl: string,
    fieldName = 'image',
    options: UploadOptions = {}
  ): Promise<UploadResult> => {
    dispatch({ type: UPLOAD_START })

    // Validate file
    const validation = validateFile(file, options)
    if (!validation.isValid) {
      dispatch({ type: UPLOAD_ERROR, payload: validation.error })
      return { success: false, error: validation.error }
    }

    const formData = new FormData()
    formData.append(fieldName, file)

    // Add any additional form data
    if (options.additionalData) {
      Object.entries(options.additionalData).forEach(([key, value]) => {
        formData.append(key, value)
      })
    }

    try {
      const response = await api.instance.upload.post(uploadUrl, formData, {
        onUploadProgress: progressEvent => {
          const progress =
            progressEvent && progressEvent.total ? Math.round((progressEvent.loaded * 100) / progressEvent?.total) : 0
          dispatch({ type: UPLOAD_PROGRESS, payload: progress })
        },
        ...options.axiosConfig,
      })

      dispatch({ type: UPLOAD_SUCCESS })
      return { success: true, data: response.data }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Upload failed'
      dispatch({ type: UPLOAD_ERROR, payload: errorMessage })
      return { success: false, error: errorMessage }
    }
  }

  // Delete image function
  const deleteImage = async (deleteUrl: string, options: DeleteOptions = {}): Promise<UploadResult> => {
    dispatch({ type: UPLOAD_START })

    try {
      const response = await axios.delete(deleteUrl, {
        headers: options.headers,
        ...options.axiosConfig,
      })

      dispatch({ type: UPLOAD_SUCCESS })
      return { success: true, data: response.data }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Delete failed'
      dispatch({ type: UPLOAD_ERROR, payload: errorMessage })
      return { success: false, error: errorMessage }
    }
  }

  // File validation function
  const validateFile = (file: File, options: UploadOptions = {}): ValidationResult => {
    const {
      maxSize = 2 * 1024 * 1024, // 2MB default
      allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'],
    } = options

    // Check file type
    if (!allowedTypes.includes(file.type)) {
      return {
        isValid: false,
        error: `Invalid file type. Allowed types: ${allowedTypes.join(', ')}`,
      }
    }

    // Check file size
    if (file.size > maxSize) {
      const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(1)
      return {
        isValid: false,
        error: `File size must be less than ${maxSizeMB}MB`,
      }
    }

    // For images, we could add dimension checking here if needed
    // This would require creating an Image object and checking naturalWidth/naturalHeight

    return { isValid: true }
  }

  // Clear functions
  const clearError = () => dispatch({ type: CLEAR_ERROR })
  const clearSuccess = () => dispatch({ type: CLEAR_SUCCESS })
  const reset = () => dispatch({ type: RESET })

  const value: ImageUploadContextType = {
    ...state,
    uploadImage,
    deleteImage,
    validateFile,
    clearError,
    clearSuccess,
    reset,
  }

  return <ImageUploadContext.Provider value={value}>{children}</ImageUploadContext.Provider>
})

// Hook to use the context
export const useImageUpload = (): ImageUploadContextType => {
  const context = useContext(ImageUploadContext)
  if (!context) {
    throw new Error('useImageUpload must be used within an ImageUploadProvider')
  }
  return context
}
